import { useRouter } from "next/router";
import { useSetRecoilState } from "recoil";

import {
  GetCartDocument,
  GetCartQuery,
  GetCartQueryVariables,
  OrderDto,
  OrderStatus,
  useChangeNewPriceOrderMutation,
  useInsertCartItemByIdMutation,
  useUpdateOrderMutation
} from "graphql/generated";
import graphQLClient from "graphql/react-query";

import { cartState } from "../states";
import { getTracking, getUtm } from "../utils/tracking";

export const useContinueEDTrial = (
  order: Pick<OrderDto, "sysId" | "status" | "stripeSubscriptionId" | "orderPriceId">,
  productId: string
) => {
  const { push } = useRouter();
  const setCart = useSetRecoilState(cartState);
  const { mutateAsync: changePrice } = useChangeNewPriceOrderMutation(graphQLClient, {});
  const { mutateAsync: updateOrder } = useUpdateOrderMutation(graphQLClient, {});
  const { mutateAsync: insertCartAsync } = useInsertCartItemByIdMutation(graphQLClient, {});
  const getCart = async () => {
    const res = await graphQLClient.request<GetCartQuery, GetCartQueryVariables>(GetCartDocument, {
      input: {
        shouldReturnCalculation: true
      }
    });
    return res.getCart;
  };

  const insertCart = async (priceId: string) => {
    const cart = await getCart();
    await insertCartAsync(
      {
        input: {
          sysId: cart.sysId,
          productId,
          selectedPriceId: priceId,
          shouldReturnCalculation: true,
          oldOrder: {
            id: order.sysId,
            isCancelled: order.status === OrderStatus.Cancel,
            subId: order.stripeSubscriptionId,
            status: order.status,
            priceId: order.orderPriceId,
            isSkippedEval: true
          },
          fbc: localStorage.fbc,
          fbp: localStorage.fbp,
          utm: getUtm(),
          tracking: getTracking()
        }
      },
      {
        onSuccess: (data) => {
          if (data?.insertCartItemById) {
            setCart({
              cartItems: data.insertCartItemById.cartItems,
              isLoading: false
            });
          }
        }
      }
    );
  };

  const onConfirmContinueTrial = async (priceId: string) => {
    await changePrice({
      input: {
        orderId: order.sysId,
        newPriceId: priceId
      }
    });
    await updateOrder({
      input: {
        sysId: order.sysId,
        continueTrial: true
      }
    });
    await insertCart(priceId);
    await push("/checkout");
  };

  return {
    onConfirmContinueTrial
  };
};
